/**
 * ExecutiveView — the executive-facing ForecastEDGE AI surface.
 *
 * Design: executive-view-design/EXEC_VIEW_v1_design.md. Gated on
 * User.HasExecutiveAccess (0/1/2 enum). Level 1 (executive-only) users
 * get exactly two tabs and land here at /executive; level 2 (executive
 * + traditional) users reach this surface embedded behind the toggle in
 * shared/ForecastEdgeAITab on the ForecastEDGE AI tab.
 *
 * Flow: brand picker (card grid) → brand workspace (left pane with brand
 * block + scenario multi-select; question library; answer view). The
 * query path is deterministic — Trinity-curated prompts with frozen CV
 * parameterizations, scenarios late-bound from the sidebar selection.
 * No free-text chat exists on this surface by design.
 *
 * Nav is a small state machine (`destination`: 'query' | 'whatif',
 * design doc EXEC_WHATIF_v1_design.md §3) that resets to 'query' on
 * every brand change. What-If Analysis (Task 12's <WhatIfView/>) is
 * gated per D3: its curated options are fetched per brand and the nav
 * item disables when either curated set (outputs/inputs) is empty —
 * fetch failure is treated the same as empty, never blocking the rest
 * of the view. Entering What-If puts the sidebar into single-select
 * (D1): the *nav toggle itself* never mutates `scenarioIds` — What-If
 * reads `scenarioIds[0]` and Sidebar narrows the display to just that
 * id, so the full multi-selection survives an in-and-out round trip.
 * Clicking a different scenario chip while on What-If is a distinct,
 * deliberate action that does replace `scenarioIds` outright (radio
 * semantics), same as a real radio control.
 *
 * Query Forecast QUESTIONS | RECENT tab (design doc §5, §8; Task 16).
 * `qfTab` is lifted here rather than into QuestionLibrary, mirroring
 * exactly how WhatIfView holds HomeState's `homeTab` (see
 * WhatIf/WhatIfView.tsx's header + the declaration comments; QUESTIONS
 * is always the default, ruled 2026-08-23) — QuestionLibrary is
 * rendered only when `activeBrand &&
 * !activePrompt`, so opening an answer UNMOUNTS it; any tab state owned
 * there would reset on every library ↔ answer round trip (the Task 12
 * round-1 bug this deliberately avoids). Semantics match WhatIfView's
 * latch verbatim: a freshly-selected brand starts on QUESTIONS and
 * flips to RECENT exactly once, the first time that brand's recents
 * arrive non-empty; a manual switch back to QUESTIONS within the same
 * brand sticks even if recents subsequently refreshes.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';

import { useProfile } from 'src/hooks/useProfile';
import { parseId } from 'src/utils/parsing';

import {
  getExecutiveOptions,
  getExecutivePrompts,
  getExecutiveTornadoPrompts,
  getQueryRecent,
  getTornadoRecent,
  getWhatIfOptions,
} from './api';
import { executiveTheme } from './theme';
import BrandPicker from './BrandPicker';
import Sidebar from './Sidebar';
import QuestionLibrary, { QFTab } from './QuestionLibrary';
import AnswerView from './AnswerView';
import TornadoAnswerView from './TornadoAnswerView';
import WhatIfView from './WhatIf/WhatIfView';
import {
  ExecutiveBrand,
  ExecutiveDestination,
  ExecutiveQueryRecentRun,
  ExecutivePromptItem,
  ExecutiveTornadoPromptItem,
  MAX_SCENARIOS,
  TornadoPair,
  WhatIfOptions,
} from './types';

const EMPTY_WHATIF_OPTIONS: WhatIfOptions = {
  outputs: [],
  inputs: [],
  examples: [],
};

// No per-scenario default flag exists — pre-check the first scenario
// (list arrives name-sorted) so the minimum-one invariant holds from
// the first render.
const defaultScenarioSelection = (brand: ExecutiveBrand): number[] =>
  brand.scenarios.length > 0 ? [brand.scenarios[0].scenario_id] : [];

// Seed pair for a tornado prompt: the authored defaults when both ids are
// still in the brand's tagged set (a default can go stale — untagged or
// deleted since publish), else the brand's first two tagged scenarios.
// Null when the brand can't form a pair at all (<2 tagged scenarios —
// the destination is disabled in that case anyway).
const seedTornadoPair = (
  brand: ExecutiveBrand,
  prompt: ExecutiveTornadoPromptItem
): TornadoPair | null => {
  const tagged = new Set(brand.scenarios.map((s) => s.scenario_id));
  const defaults = prompt.defaults;
  if (
    defaults &&
    tagged.has(defaults.scenario_a_id) &&
    tagged.has(defaults.scenario_b_id) &&
    defaults.scenario_a_id !== defaults.scenario_b_id
  ) {
    return { aId: defaults.scenario_a_id, bId: defaults.scenario_b_id };
  }
  if (brand.scenarios.length >= 2) {
    return {
      aId: brand.scenarios[0].scenario_id,
      bId: brand.scenarios[1].scenario_id,
    };
  }
  return null;
};

interface IExecutiveViewProps {
  // Rendered inside ForecastEdgeAITab's toggle layout: fill the parent
  // box instead of sizing against the viewport.
  embedded?: boolean;
  // False while the surface is mounted-but-hidden behind the
  // analyst/executive toggle. On each false→true transition the brand
  // list and (if a brand is open) its prompt list re-fetch silently, so
  // prompts saved or brands enabled from the analyst side appear
  // without a page refresh. Standalone /executive is always active.
  active?: boolean;
}

const ExecutiveView: React.FC<IExecutiveViewProps> = ({
  embedded = false,
  active = true,
}) => {
  const profile = useProfile();
  const location = useLocation();

  const clientId = useMemo<number | null>(() => {
    if (
      profile?.User?.ClientID !== null &&
      profile?.User?.ClientID !== undefined
    ) {
      return profile.User.ClientID;
    }
    const params = new URLSearchParams(location.search);
    return parseId(params.get('client')) ?? null;
  }, [profile, location.search]);

  const [brands, setBrands] = useState<ExecutiveBrand[] | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  const [activeBrand, setActiveBrand] = useState<ExecutiveBrand | null>(null);
  const [scenarioIds, setScenarioIds] = useState<number[]>([]);

  const [prompts, setPrompts] = useState<ExecutivePromptItem[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [promptsError, setPromptsError] = useState<string | null>(null);

  const [activePrompt, setActivePrompt] = useState<ExecutivePromptItem | null>(
    null
  );

  // QF QUESTIONS | RECENT tab (design doc §5, §8; see file header). Lifted
  // here so it survives QuestionLibrary's mount/unmount across an answer
  // round trip. QUESTIONS is always the default (ruled 2026-08-23 — the
  // old one-shot "flip to RECENT once history exists" latch is gone);
  // a manual tab choice still persists across round trips within a brand.
  const [qfTab, setQfTab] = useState<QFTab>('questions');

  const [queryRecents, setQueryRecents] = useState<ExecutiveQueryRecentRun[]>(
    []
  );
  // Stale-fetch guard for the recents fetch, same idiom as
  // whatIfOptionsRequestRef below: holds the model_instance_id whose
  // recents fetch is currently authoritative, so a slower-resolving
  // request from a brand already navigated away from can't clobber
  // queryRecents for the brand now active.
  const queryRecentsRequestRef = useRef<number | null>(null);

  // Fetches QF recents for `modelInstanceId`, guarded against staleness.
  // A fetch failure hides the tab (empty list) rather than surfacing an
  // error — recents are a convenience surface, never a blocking one
  // (§6 tone, same treatment as WhatIfView's getWhatIfRecent).
  const loadQueryRecent = useCallback((modelInstanceId: number) => {
    queryRecentsRequestRef.current = modelInstanceId;
    getQueryRecent(modelInstanceId)
      .then((resp) => {
        if (queryRecentsRequestRef.current === modelInstanceId) {
          setQueryRecents(resp.runs ?? []);
        }
      })
      .catch(() => {
        if (queryRecentsRequestRef.current === modelInstanceId) {
          setQueryRecents([]);
        }
      });
  }, []);

  // Nav state machine (design doc EXEC_WHATIF_v1_design.md §3). Resets
  // to 'query' on every brand change (handleSelectBrand / handleChangeBrand).
  const [destination, setDestination] = useState<ExecutiveDestination>('query');

  // ----- Tornado destination state ------------------------------------
  // Prompt library for the destination (tool='executive_tornado'), with
  // the same stale-fetch ref guard idiom as whatIfOptionsRequestRef.
  // A fetch failure disables the nav item, never blocks the view.
  const [tornadoPrompts, setTornadoPrompts] = useState<
    ExecutiveTornadoPromptItem[]
  >([]);
  const tornadoPromptsRequestRef = useRef<number | null>(null);
  const [activeTornadoPrompt, setActiveTornadoPrompt] =
    useState<ExecutiveTornadoPromptItem | null>(null);
  // ANALYSES | RECENT tab, lifted for the same unmount reason as qfTab.
  const [tornadoTab, setTornadoTab] = useState<QFTab>('questions');
  const [tornadoRecents, setTornadoRecents] = useState<
    ExecutiveQueryRecentRun[]
  >([]);
  const tornadoRecentsRequestRef = useRef<number | null>(null);
  // Draft (bound to the pane dropdowns) vs committed (what actually runs).
  // A dropdown change alone never re-runs — tornado runs are slow async
  // calculations, so the pane's Update button copies draft → committed
  // and the answer view re-runs off the committed pair.
  const [tornadoDraftPair, setTornadoDraftPair] = useState<TornadoPair | null>(
    null
  );
  const [tornadoCommittedPair, setTornadoCommittedPair] =
    useState<TornadoPair | null>(null);
  // Seeding rule: each prompt's authored defaults seed the dropdowns
  // until the executive explicitly changes the pair; after that their
  // choice persists across prompts within the brand.
  const tornadoPairTouchedRef = useRef(false);

  const loadTornadoPrompts = useCallback((modelInstanceId: number) => {
    tornadoPromptsRequestRef.current = modelInstanceId;
    getExecutiveTornadoPrompts(modelInstanceId)
      .then((resp) => {
        if (tornadoPromptsRequestRef.current === modelInstanceId) {
          setTornadoPrompts(resp.prompts ?? []);
        }
      })
      .catch(() => {
        if (tornadoPromptsRequestRef.current === modelInstanceId) {
          setTornadoPrompts([]);
        }
      });
  }, []);

  const loadTornadoRecent = useCallback((modelInstanceId: number) => {
    tornadoRecentsRequestRef.current = modelInstanceId;
    getTornadoRecent(modelInstanceId)
      .then((resp) => {
        if (tornadoRecentsRequestRef.current === modelInstanceId) {
          setTornadoRecents(resp.runs ?? []);
        }
      })
      .catch(() => {
        if (tornadoRecentsRequestRef.current === modelInstanceId) {
          setTornadoRecents([]);
        }
      });
  }, []);

  // What-If curated options, fetched per brand (D3: enablement is
  // bundled — the nav item is live exactly when both curated sets are
  // non-empty). A fetch failure disables the nav item rather than
  // blocking the rest of the view (brief: "do NOT block the rest of the
  // view on it").
  const [whatIfOptions, setWhatIfOptions] =
    useState<WhatIfOptions>(EMPTY_WHATIF_OPTIONS);
  // Tracks which brand's options fetch is currently authoritative, so a
  // slower-resolving request from a brand the executive already
  // navigated away from can't clobber whatIfOptions for the brand now
  // active (same stale-fetch problem the getExecutiveOptions effect
  // above guards against with its `cancelled` flag; this is a handler,
  // not an effect, so it's a ref comparison instead of a closure flag).
  const whatIfOptionsRequestRef = useRef<number | null>(null);

  useEffect(() => {
    if (clientId == null) return undefined;
    let cancelled = false;
    setBrands(null);
    setOptionsError(null);
    getExecutiveOptions(clientId)
      .then((resp) => {
        if (!cancelled) setBrands(resp.brands ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          setOptionsError(
            'Brands could not be loaded right now. Please try again shortly.'
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // silent=true refreshes in the background: keeps whatever is on
  // screen (list or answer view) in place and swaps the data in.
  const loadPrompts = useCallback((modelInstanceId: number, silent = false) => {
    if (!silent) {
      setPrompts([]);
      setPromptsError(null);
      setPromptsLoading(true);
    }
    getExecutivePrompts(modelInstanceId)
      .then((resp) => {
        setPrompts(resp.prompts ?? []);
        if (silent) setPromptsError(null);
      })
      .catch(() => {
        if (!silent) {
          setPromptsError(
            'Questions could not be loaded right now. Please try again shortly.'
          );
        }
      })
      .finally(() => {
        if (!silent) setPromptsLoading(false);
      });
  }, []);

  const handleSelectBrand = useCallback(
    (brand: ExecutiveBrand) => {
      setActiveBrand(brand);
      setScenarioIds(defaultScenarioSelection(brand));
      setActivePrompt(null);
      setDestination('query');
      loadPrompts(brand.model_instance_id);

      // Per-brand reset (Task 12 round-1 lesson, file header): a fresh
      // brand starts on QUESTIONS — never inherit the previous brand's
      // tab state.
      setQfTab('questions');
      setQueryRecents([]);
      loadQueryRecent(brand.model_instance_id);

      // Tornado destination resets per brand: fresh library, recents,
      // untouched pair (each prompt's defaults seed the dropdowns again).
      setActiveTornadoPrompt(null);
      setTornadoTab('questions');
      setTornadoRecents([]);
      setTornadoDraftPair(null);
      setTornadoCommittedPair(null);
      tornadoPairTouchedRef.current = false;
      loadTornadoPrompts(brand.model_instance_id);
      loadTornadoRecent(brand.model_instance_id);

      // Stale-fetch guard: if the executive picks brand A then brand B
      // before A's options response lands, A's response must not
      // overwrite whatIfOptions for the now-active brand B.
      const requestedInstanceId = brand.model_instance_id;
      whatIfOptionsRequestRef.current = requestedInstanceId;
      setWhatIfOptions(EMPTY_WHATIF_OPTIONS);
      getWhatIfOptions(requestedInstanceId)
        .then((options) => {
          if (whatIfOptionsRequestRef.current === requestedInstanceId) {
            setWhatIfOptions(options);
          }
        })
        .catch(() => {
          if (whatIfOptionsRequestRef.current === requestedInstanceId) {
            // Treat as disabled — never block the rest of the view on
            // this fetch (design doc D3, §6 "Options load fails").
            setWhatIfOptions(EMPTY_WHATIF_OPTIONS);
          }
        });
    },
    [loadPrompts, loadQueryRecent, loadTornadoPrompts, loadTornadoRecent]
  );

  const whatIfEnabled =
    whatIfOptions.outputs.length > 0 &&
    whatIfOptions.inputs.length > 0 &&
    (activeBrand?.scenarios.length ?? 0) > 0;

  // A pair needs two scenarios; with fewer, the destination stays
  // disabled no matter how many prompts are published.
  const tornadoEnabled =
    tornadoPrompts.length > 0 && (activeBrand?.scenarios.length ?? 0) >= 2;

  const handleNavigate = useCallback(
    (next: ExecutiveDestination) => {
      setDestination(next);
      // Entering the tornado destination with pristine dropdowns: seed
      // the draft so the pane never renders empty selects. First
      // prompt's authored defaults, else the first two tagged scenarios.
      if (next === 'tornado' && !tornadoDraftPair && activeBrand) {
        const firstPrompt = tornadoPrompts[0];
        const pair = firstPrompt
          ? seedTornadoPair(activeBrand, firstPrompt)
          : activeBrand.scenarios.length >= 2
            ? {
                aId: activeBrand.scenarios[0].scenario_id,
                bId: activeBrand.scenarios[1].scenario_id,
              }
            : null;
        if (pair) setTornadoDraftPair(pair);
      }
    },
    [activeBrand, tornadoDraftPair, tornadoPrompts]
  );

  // Opening a tornado prompt: seed the pair from the prompt's authored
  // defaults unless the executive has already made their own choice
  // (then the current draft becomes what runs). Committed is set here so
  // the answer view runs immediately on open.
  const handleSelectTornadoPrompt = useCallback(
    (prompt: ExecutivePromptItem) => {
      if (!activeBrand) return;
      const tornadoPrompt = prompt as ExecutiveTornadoPromptItem;
      let pair: TornadoPair | null;
      if (tornadoPairTouchedRef.current && tornadoDraftPair) {
        pair = tornadoDraftPair;
      } else {
        pair = seedTornadoPair(activeBrand, tornadoPrompt);
      }
      if (!pair) return; // <2 tagged scenarios — nav is disabled anyway
      setTornadoDraftPair(pair);
      setTornadoCommittedPair(pair);
      setActiveTornadoPrompt(tornadoPrompt);
    },
    [activeBrand, tornadoDraftPair]
  );

  const handleTornadoPairChange = useCallback((pair: TornadoPair) => {
    tornadoPairTouchedRef.current = true;
    setTornadoDraftPair(pair);
  }, []);

  // The pane's Update button: armed only on an open answer whose draft
  // differs from the pair that last ran.
  const tornadoUpdateArmed =
    destination === 'tornado' &&
    activeTornadoPrompt !== null &&
    tornadoDraftPair !== null &&
    tornadoCommittedPair !== null &&
    tornadoDraftPair.aId !== tornadoDraftPair.bId &&
    (tornadoDraftPair.aId !== tornadoCommittedPair.aId ||
      tornadoDraftPair.bId !== tornadoCommittedPair.bId);

  const handleTornadoUpdate = useCallback(() => {
    setTornadoCommittedPair(tornadoDraftPair);
  }, [tornadoDraftPair]);

  const handleBackFromTornadoAnswer = useCallback(() => {
    setActiveTornadoPrompt(null);
    if (activeBrand) {
      loadTornadoRecent(activeBrand.model_instance_id);
    }
  }, [activeBrand, loadTornadoRecent]);

  // Re-fetch on toggle-in (see the `active` prop doc). Silent on both
  // counts: the brand list swaps in place (no loading flash on the
  // picker) and the prompt list updates without disturbing an open
  // answer view.
  const prevActiveRef = useRef(active);
  useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = active;
    if (!active || wasActive) return;
    if (clientId != null) {
      getExecutiveOptions(clientId)
        .then((resp) => setBrands(resp.brands ?? []))
        .catch(() => {
          // Keep showing the last-known brand list on a failed refresh.
        });
    }
    if (activeBrand) {
      loadPrompts(activeBrand.model_instance_id, true);
      // Tornado prompts published from the analyst side appear on
      // toggle-in the same way (its loader is already silent/guarded).
      loadTornadoPrompts(activeBrand.model_instance_id);
    }
  }, [active, activeBrand, clientId, loadPrompts, loadTornadoPrompts]);

  // Merely navigating to What-If never touches scenarioIds — Sidebar
  // narrows the *display* to selectedScenarioIds[0] on its own (design
  // doc D1); the full multi-selection survives an in-and-out round trip
  // untouched. Clicking a scenario chip while on What-If is a distinct,
  // deliberate action: per the brief it replaces the selection outright
  // (`setScenarioIds([id])`), same as a real radio control — the
  // survives-round-trip guarantee is about navigation, not chip clicks.
  const handleToggleScenario = useCallback(
    (scenarioId: number) => {
      if (destination === 'whatif') {
        setScenarioIds([scenarioId]);
        return;
      }
      setScenarioIds((prev) => {
        if (prev.includes(scenarioId)) {
          // Minimum one selected — unchecking the last is a no-op.
          if (prev.length === 1) return prev;
          return prev.filter((id) => id !== scenarioId);
        }
        if (prev.length >= MAX_SCENARIOS) return prev;
        return [...prev, scenarioId];
      });
    },
    [destination]
  );

  const handleChangeBrand = useCallback(() => {
    setActiveBrand(null);
    setActivePrompt(null);
    setDestination('query');
    setWhatIfOptions(EMPTY_WHATIF_OPTIONS);
    // No brand is authoritative on the picker — invalidate so a pending
    // options fetch from the brand just left can't land here.
    whatIfOptionsRequestRef.current = null;
    // Same invalidation for QF recents, plus the tab reset (file
    // header) — the picker has no brand for either to mean anything.
    queryRecentsRequestRef.current = null;
    setQfTab('questions');
    setQueryRecents([]);
    // Tornado state means nothing on the picker either.
    tornadoPromptsRequestRef.current = null;
    tornadoRecentsRequestRef.current = null;
    setTornadoPrompts([]);
    setActiveTornadoPrompt(null);
    setTornadoTab('questions');
    setTornadoRecents([]);
    setTornadoDraftPair(null);
    setTornadoCommittedPair(null);
    tornadoPairTouchedRef.current = false;
  }, []);

  // Refresh QF recents after an answer view records a new run: the
  // cheapest honest signal available is "the executive returned from an
  // answer to the library" (activePrompt clearing), since a prompt
  // selection always fires record: true (AnswerView.tsx) and a
  // scenario-toggle re-run never adds a row (design doc §8 append-only).
  // Silent — never disturbs the currently-rendered tab/list while it
  // refetches.
  const handleBackFromAnswer = useCallback(() => {
    setActivePrompt(null);
    if (activeBrand) {
      loadQueryRecent(activeBrand.model_instance_id);
    }
  }, [activeBrand, loadQueryRecent]);

  // Route-level guard, mirroring AgenticV2's HasAgenticAccess pattern.
  // Any non-zero level grants the surface (1 = executive-only,
  // 2 = executive + traditional).
  if (
    profile?.User?.HasExecutiveAccess !== 1 &&
    profile?.User?.HasExecutiveAccess !== 2
  ) {
    return <Navigate to="/" replace />;
  }

  return (
    <ThemeProvider theme={executiveTheme}>
      <Box
        sx={{
          height: embedded ? '100%' : 'calc(100vh - 120px)',
          display: 'flex',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {/* The pane is a constant of the surface (2026-08-16 UI review):
            it renders on every state, including the brand picker, where
            Query Forecast sits disabled until a brand is chosen. */}
        <Sidebar
          brand={activeBrand}
          selectedScenarioIds={scenarioIds}
          onToggleScenario={handleToggleScenario}
          onBrandPortfolio={handleChangeBrand}
          destination={destination}
          onNavigate={handleNavigate}
          whatIfEnabled={whatIfEnabled}
          tornadoEnabled={tornadoEnabled}
          scenarioMode={
            destination === 'tornado'
              ? 'pair'
              : destination === 'whatif'
                ? 'single'
                : 'multi'
          }
          tornadoPair={tornadoDraftPair}
          onTornadoPairChange={handleTornadoPairChange}
          tornadoUpdateArmed={tornadoUpdateArmed}
          onTornadoUpdate={handleTornadoUpdate}
        />

        <Box sx={{ flex: 1, overflowY: 'auto', minWidth: 0, display: 'flex' }}>
          {clientId == null && (
            <Box sx={{ p: 6, mx: 'auto' }}>
              <Alert severity="info">
                No client context is available for this account. Please contact
                your Trinity team.
              </Alert>
            </Box>
          )}

          {clientId != null && optionsError && (
            <Box sx={{ p: 6, mx: 'auto' }}>
              <Alert severity="info">{optionsError}</Alert>
            </Box>
          )}

          {clientId != null && !optionsError && brands === null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mx: 'auto',
                py: 10,
              }}
            >
              <CircularProgress size={22} />
              <Typography variant="body1" color="text.secondary">
                Loading…
              </Typography>
            </Box>
          )}

          {clientId != null &&
            !optionsError &&
            brands !== null &&
            !activeBrand && (
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                <BrandPicker
                  brands={brands}
                  greetingName={profile.User.Display_Name?.split(' ')[0]}
                  onSelect={handleSelectBrand}
                />
              </Box>
            )}

          {clientId != null && !optionsError && activeBrand && (
            <Box sx={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
              {destination === 'whatif' && scenarioIds[0] !== undefined ? (
                <WhatIfView
                  brand={activeBrand}
                  scenarioId={scenarioIds[0]}
                  options={whatIfOptions}
                />
              ) : destination === 'tornado' ? (
                activeTornadoPrompt && tornadoCommittedPair ? (
                  <TornadoAnswerView
                    brand={activeBrand}
                    prompt={activeTornadoPrompt}
                    pair={tornadoCommittedPair}
                    onBack={handleBackFromTornadoAnswer}
                  />
                ) : (
                  <QuestionLibrary
                    prompts={tornadoPrompts}
                    loading={false}
                    error={null}
                    onSelect={handleSelectTornadoPrompt}
                    recents={tornadoRecents}
                    activeTab={tornadoTab}
                    onChangeTab={setTornadoTab}
                    heading="What's driving the forecast?"
                    subheading="Pick an analysis"
                    emptyCopy="No analyses are configured for this brand yet. Please contact your Trinity team."
                  />
                )
              ) : activePrompt ? (
                <AnswerView
                  brand={activeBrand}
                  prompt={activePrompt}
                  scenarioIds={scenarioIds}
                  onBack={handleBackFromAnswer}
                />
              ) : (
                <QuestionLibrary
                  prompts={prompts}
                  loading={promptsLoading}
                  error={promptsError}
                  onSelect={setActivePrompt}
                  recents={queryRecents}
                  activeTab={qfTab}
                  onChangeTab={setQfTab}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ExecutiveView;
